import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import HttpError from "@/lib/api/httpError";

import { QueryKeys } from "@/queries/utils";
import { CandidateApplicationStatus, JobOpeningStatus } from "@/types";
import { AdminSummary } from "@/queries/admin/dashboard";
import { AdminPipelineJob } from "@/queries/admin/job-pipeline";
import { AdminJobDetails } from "@/queries/admin/job-details";
import { AdminCompanyDetails } from "@/queries/admin/company-details";

type SetJobStatusParams =
  | {
      jobId: string;
      status?: JobOpeningStatus;
      currentStatus: JobOpeningStatus;
    }
  | {
      jobId: string;
      status: JobOpeningStatus;
      currentStatus?: JobOpeningStatus;
    };

const NEXT_STATUS_MAP: Record<JobOpeningStatus, JobOpeningStatus> = {
  Draft: "New Opening",
  "New Opening": "Creating Job Spec",
  "Creating Job Spec": "Identifying Candidates",
  "Identifying Candidates": "Presenting Shortlist",
  "Presenting Shortlist": "Role Filled (won)",
  "Role Filled (won)": "Role Filled (won)",
  "Role Filled (lost)": "Role Filled (lost)",
};

const finalStates = new Set<CandidateApplicationStatus>([
  "Accepted by Client",
  "Rejected for Role",
  "Not Interested",
]);
const atFinalState = (state: CandidateApplicationStatus) =>
  finalStates.has(state);

export const useSetJobStatus = () => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      jobId,
      status,
      currentStatus,
    }: SetJobStatusParams) => {
      const token = await getAccessTokenSilently();

      return request<AdminPipelineJob>({
        method: "PATCH",
        path: `/api/admin/job_opening/${jobId}`,
        token,
        body: JSON.stringify({
          status: status ?? NEXT_STATUS_MAP[currentStatus],
        }),
      });
    },
    onSuccess: (data) => {
      // Check if the job reached a final stage
      const done =
        data.status === "Role Filled (won)" ||
        data.status === "Role Filled (lost)";

      // Fetch any existing data
      const prevPipeline = qc.getQueryData<AdminPipelineJob[]>(
        QueryKeys.adminJobPipeline,
      );

      // If there is existing data, update it
      if (prevPipeline) {
        // Make a copy and check find the updated element
        const newData = [...prevPipeline];
        const itemIndex = newData.findIndex((entry) => entry.id === data.id);

        // If the element is found
        if (itemIndex !== -1 && newData[itemIndex]) {
          newData[itemIndex] = data;

          qc.setQueryData(QueryKeys.adminJobPipeline, newData);
        } else {
          // If the entry couldn't be found, invalidate and fetch new data
          void qc.invalidateQueries({
            queryKey: QueryKeys.adminJobPipeline,
          });
        }
      }

      const prevDetails = qc.getQueryData<AdminJobDetails>(
        QueryKeys.adminJobDetails(data.id),
      );

      // If there is existing data, update it
      if (prevDetails) {
        // Update the data
        if (done && prevDetails.status !== data.status) {
          // If we transitioned to a final state, candidates were rejected and data must be updated

          // Make a best-effort to move candidates to the right state
          qc.setQueryData(QueryKeys.adminJobDetails(data.id), {
            ...prevDetails,
            candidates: prevDetails.candidates.map((candidate) => ({
              ...candidate,
              opportunityStatus: atFinalState(candidate.opportunityStatus)
                ? candidate.opportunityStatus
                : "Rejected for Role",
              opportunityStatusBeforeRoleClosed: candidate.opportunityStatus,
            })),
            ...data,
          });
        } else {
          qc.setQueryData(QueryKeys.adminJobDetails(data.id), {
            ...prevDetails,
            ...data,
          });
        }
      }

      const prevCompany = qc.getQueryData<AdminCompanyDetails>(
        QueryKeys.adminCompany(data.company.id),
      );

      // If there is existing data, update it
      if (prevCompany) {
        // Make a copy and check find the updated element
        const newData = [...prevCompany.jobs];
        const itemIndex = newData.findIndex((entry) => entry.id === data.id);

        // If the element is found
        if (itemIndex !== -1 && newData[itemIndex]) {
          newData[itemIndex] = data;

          qc.setQueryData(QueryKeys.adminCompany(data.company.id), {
            ...prevCompany,
            jobs: newData,
          });
        } else {
          // If the entry couldn't be found, invalidate and fetch new data
          void qc.invalidateQueries({
            queryKey: QueryKeys.adminCompany(data.company.id),
          });
        }
      }

      // If the candidate reached a final stage, update the dashboard number
      if (done) {
        const dashboardPrev = qc.getQueryData<AdminSummary>(
          QueryKeys.adminDashboard,
        );

        if (dashboardPrev) {
          qc.setQueryData(QueryKeys.adminDashboard, {
            ...dashboardPrev,
            openJobsCount: dashboardPrev.openJobsCount - 1,
          });
        }
      }
    },
    onError(error) {
      if (error instanceof HttpError) {
        toast.error(error.message);
      }
    },
  });

  return {
    setJobStatus: mutate,
    isSettingJobStatus: isPending,
    setJobStatusError: error,
  };
};
